import { I as Icon, i as iconDefinition } from './definition28.js';
import { V as VividElement, a as attr, o as observable, h as html, d as createRegisterFunction, f as defineVividComponent } from './vivid-element.js';
import { a as WithFeedback, f as feedbackMessageDefinition } from './mixins.js';
import { D as DelegatesAria, d as delegateAria } from './delegates-aria.js';
import { C as CheckableFormAssociated } from './form-associated.js';
import { W as WithErrorText } from './with-error-text.js';
import { W as WithSuccessText } from './with-success-text.js';
import { F as FormElement } from './form-element.js';
import { c as classNames } from './class-names.js';
import { w as when } from './when.js';
import { s as slotted } from './slotted.js';

const styles = ":host{display:inline-flex;flex-direction:column;gap:4px;vertical-align:middle;--_low-ink-color: var(--vvd-color-neutral-600)}:host([disabled]){cursor:not-allowed}.base.connotation-cta{--_connotation-color-primary: var(--vvd-checkbox-cta-primary, var(--vvd-color-cta-500));--_connotation-color-primary-text: var(--vvd-checkbox-cta-primary-text, var(--vvd-color-canvas));--_connotation-color-primary-increment: var(--vvd-checkbox-cta-primary-increment, var(--vvd-color-cta-600));--_connotation-color-fierce: var(--vvd-checkbox-cta-fierce, var(--vvd-color-cta-700));--_connotation-color-intermediate: var(--vvd-checkbox-cta-intermediate, var(--vvd-color-cta-500));--_connotation-color-firm: var(--vvd-checkbox-cta-firm, var(--vvd-color-cta-600))}.base:not(.connotation-cta){--_connotation-color-primary: var(--vvd-checkbox-accent-primary, var(--vvd-color-canvas-text));--_connotation-color-primary-text: var(--vvd-checkbox-accent-primary-text, var(--vvd-color-canvas));--_connotation-color-primary-increment: var(--vvd-checkbox-accent-primary-increment, var(--vvd-color-neutral-800));--_connotation-color-fierce: var(--vvd-checkbox-accent-fierce, var(--vvd-color-neutral-700));--_connotation-color-intermediate: var(--vvd-checkbox-accent-intermediate, var(--vvd-color-neutral-500));--_connotation-color-firm: var(--vvd-checkbox-accent-firm, var(--vvd-color-canvas-text))}.base{--_appearance-color-text: var(--vvd-color-canvas-text);--_appearance-color-fill: var(--vvd-color-canvas);--_appearance-color-outline: var(--vvd-color-neutral-500)}.base:where(.hover,:hover):where(:not(.disabled,:disabled,.readonly)){--_appearance-color-text: var(--vvd-color-canvas-text);--_appearance-color-fill: var(--vvd-color-canvas);--_appearance-color-outline: var(--vvd-color-neutral-700)}.base:where(.checked,:checked):where(:not(.disabled,:disabled)){--_appearance-color-text: var(--_connotation-color-primary-text);--_appearance-color-fill: var(--_connotation-color-primary);--_appearance-color-outline: var(--_connotation-color-primary)}.base:where(.checked,:checked):where(.hover,:hover):where(:not(.disabled,:disabled,.readonly)){--_appearance-color-text: var(--_connotation-color-primary-text);--_appearance-color-fill: var(--_connotation-color-primary-increment);--_appearance-color-outline: var(--_connotation-color-primary-increment)}.base:where(.disabled,:disabled){--_appearance-color-text: var(--vvd-color-neutral-300);--_appearance-color-fill: var(--vvd-color-neutral-100);--_appearance-color-outline: var(--vvd-color-neutral-300)}.base:where(.readonly):where(:not(.disabled,:disabled)){--_appearance-color-text: var(--vvd-color-canvas-text);--_appearance-color-fill: var(--vvd-color-neutral-200);--_appearance-color-outline: var(--vvd-color-neutral-400)}.base{display:inline-flex;align-items:center;gap:8px;vertical-align:middle}.base:focus-visible{outline:none}@supports (user-select: none){.base{user-select:none}}.base:not(.disabled){cursor:pointer}.base.disabled{pointer-events:none}.control{position:relative;display:flex;flex-shrink:0;align-items:center;justify-content:center;border-radius:4px;background-color:var(--_appearance-color-fill);block-size:calc(1px*(20 + 4*clamp(-1,var(--vvd-size-density, 0),2)));box-shadow:inset 0 0 0 2px var(--_appearance-color-outline);color:var(--_appearance-color-text);inline-size:calc(1px*(20 + 4*clamp(-1,var(--vvd-size-density, 0),2)))}.base:focus-visible .control{--focus-stroke-gap-color: transparent}.base:focus-visible .control:after{box-shadow:0 0 0 4px color-mix(in srgb,var(--vvd-color-cta-500),transparent 85%),inset 0 0 0 3px var(--focus-stroke-gap-color, currentColor);outline:1px solid var(--focus-stroke-color, var(--vvd-color-cta-500));outline-offset:calc(-1px - var(--focus-inset, 0px));position:absolute;display:block;border-radius:var(--focus-border-radius, inherit);block-size:calc(100% + var(--focus-block-size-addition, 4px));content:\"\";inline-size:calc(100% + var(--focus-block-size-addition, 4px));inset-block-start:50%;inset-inline-start:50%;transform:translate(-50%,-50%)}.base.error:not(.checked) .control{background-color:var(--vvd-color-canvas);box-shadow:inset 0 0 0 2px var(--vvd-color-alert-500)}.indicator{font-size:calc(1px*(20 + 4*clamp(-1,var(--vvd-size-density, 0),2)) - 4px)}.base:not(.checked) .indicator.checkmark{display:none}.base.checked .indicator.minus,.base:not(.indeterminate) .indicator.minus{display:none}label{display:flex;flex-wrap:wrap;align-items:center;color:var(--vvd-color-canvas-text);column-gap:4px;cursor:pointer;font:var(--vvd-typography-base)}.hide-label label{display:none}.icon{font-size:calc(1px*(20 + 4*clamp(-1,var(--vvd-size-density, 0),2))*.6);line-height:1}";

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
const keySpace = " ";
class Checkbox extends DelegatesAria(
  WithFeedback(
    WithErrorText(
      WithSuccessText(FormElement(CheckableFormAssociated(VividElement)))
    )
  )
) {
  constructor() {
    super();
    this.tabindex = null;
    /**
     * @internal
     */
    this.proxy = document.createElement("input");
    /**
     * The element's value to be included in form submission when checked.
     * Default to "on" to reach parity with input[type="checkbox"]
     *
     * @internal
     */
    this.initialValue = "on";
    this.defaultSlottedNodes = [];
    this.indeterminate = false;
    /**
     * @internal
     */
    this.keypressHandler = (event) => {
      if (event.target instanceof HTMLAnchorElement) {
        return true;
      }
      if (!this.disabled && !this.readOnly) {
        switch (event.key) {
          case keySpace:
            if (this.indeterminate) {
              this.indeterminate = false;
            }
            this.checked = !this.checked;
            break;
        }
      }
      return false;
    };
    /**
     * @internal
     */
    this.clickHandler = (event) => {
      if (event.target instanceof HTMLAnchorElement) {
        return true;
      }
      if (!this.disabled && !this.readOnly) {
        if (this.indeterminate) {
          this.indeterminate = false;
        }
        this.checked = !this.checked;
      }
      return false;
    };
    this.proxy.setAttribute("type", "checkbox");
  }
  // Map to proxy element
  /**
   * @internal
   */
  readOnlyChanged() {
    if (this.proxy instanceof HTMLInputElement) {
      this.proxy.readOnly = this.readOnly;
    }
  }
  ariaCheckedChanged() {
    if (this.ariaChecked === "mixed") {
      this.indeterminate = true;
    } else {
      this.indeterminate = false;
      this.checked = this.ariaChecked === "true" ? true : false;
    }
  }
  /**
   * @internal
   */
  checkedChanged(prev, next) {
    super.checkedChanged(prev, next);
    this.ariaChecked = next == true ? "true" : "false";
    if (prev !== void 0) {
      this.$emit("input");
    }
  }
}
__decorateClass([
  attr({ attribute: "tabindex" })
], Checkbox.prototype, "tabindex");
__decorateClass([
  attr
], Checkbox.prototype, "connotation");
__decorateClass([
  attr({ attribute: "readonly", mode: "boolean" })
], Checkbox.prototype, "readOnly");
__decorateClass([
  observable
], Checkbox.prototype, "defaultSlottedNodes");
__decorateClass([
  attr({ attribute: "indeterminate", mode: "boolean" })
], Checkbox.prototype, "indeterminate");
__decorateClass([
  observable
], Checkbox.prototype, "slottedContent");

const getClasses = ({
  connotation,
  readOnly,
  checked,
  disabled,
  indeterminate,
  errorValidationMessage,
  successText,
  label,
  slottedContent
}) => classNames(
  "base",
  [`connotation-${connotation}`, Boolean(connotation)],
  ["readonly", Boolean(readOnly)],
  ["checked", Boolean(checked) || Boolean(indeterminate)],
  ["disabled", Boolean(disabled)],
  ["error connotation-alert", Boolean(errorValidationMessage)],
  ["success connotation-success", !!successText],
  ["hide-label", !label && !slottedContent?.length]
);
const CheckboxTemplate = (context) => {
  const iconTag = context.tagFor(Icon);
  return html`<template>
		<div
			class="${getClasses}"
			aria-describedby="${(x) => x._feedbackDescribedBy}"
			${delegateAria({
    role: "checkbox",
    ariaChecked: (x) => x.indeterminate ? "mixed" : x.checked,
    ariaRequired: (x) => x.required,
    ariaDisabled: (x) => x.disabled,
    ariaReadOnly: (x) => x.readOnly
  })}
			tabindex="${(x) => x.tabindex !== null ? x.tabindex : x.disabled ? null : 0}"
			@keypress="${(x, c) => x.keypressHandler(c.event)}"
			@click="${(x, c) => x.clickHandler(c.event)}"
		>
			<div class="control">
				${when(
    (x) => x.checked,
    html`<${iconTag} name="check-solid" class="icon"></${iconTag}>`
  )}
				${when(
    (x) => x.indeterminate,
    html`<${iconTag} name="minus-solid" class="icon"></${iconTag}>`
  )}
			</div>
			${html`<label
				>${(x) => x.label}<slot ${slotted("slottedContent")}></slot
			></label>`}
		</div>
		${(x) => x._getFeedbackTemplate(context)}
	</template>`;
};

const checkboxDefinition = defineVividComponent(
  "checkbox",
  Checkbox,
  CheckboxTemplate,
  [iconDefinition, feedbackMessageDefinition],
  {
    styles,
    shadowOptions: {
      delegatesFocus: true
    }
  }
);
const registerCheckbox = createRegisterFunction(checkboxDefinition);

export { Checkbox as C, checkboxDefinition as c, registerCheckbox as r };
